import React, { useState, useEffect } from 'react';
import { 
  Bell, AtSign, UserCheck, MessageCircle, Pin, Mail, 
  MessageSquare, Smartphone, Save, RefreshCw, Crown,
  CheckCircle, ToggleLeft, ToggleRight, ChevronRight, X
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * NotificationPreferences - User notification settings panel
 * Accessible from Profile menu
 */
const NotificationPreferences = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    events: {
      mentions: true,
      assigned: true,
      replies: true,
      new_pins: true
    },
    channels: {
      in_app: true,
      email: false,
      teams: false,
      whatsapp: false
    },
    daily_signal_comments: true
  });
  const [tier, setTier] = useState('business');

  const tenantId = user?.tenant?.id || user?.tenant_id;

  useEffect(() => {
    if (isOpen) {
      loadPreferences();
    }
  }, [isOpen]);

  const loadPreferences = async () => {
    if (!tenantId || !user?.id) return;
    setLoading(true);
    try {
      const [prefsRes, capsRes] = await Promise.all([
        api.getNotificationPreferences({ tenant_id: tenantId, user_id: user.id }),
        api.getCommentCapabilities(tenantId, user.id)
      ]);
      setPreferences(prefsRes.data.preferences);
      setTier(capsRes.data.tier || 'business');
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tenantId || !user?.id) return;
    setSaving(true);
    try {
      await api.updateNotificationPreferences(tenantId, user.id, preferences);
      onClose();
    } catch (err) {
      console.error('Failed to save preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateEvent = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      events: { ...prev.events, [key]: value }
    }));
  };

  const updateChannel = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      channels: { ...prev.channels, [key]: value }
    }));
  };

  const isEnterprise = tier === 'enterprise';

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-[var(--clr-surface-subtle)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-[var(--clr-brand-primary)]" />
              <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Configure how you receive comment notifications
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Event Toggles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Notify me when...
                </h3>
                <div className="space-y-3">
                  <ToggleRow
                    icon={AtSign}
                    label="I'm @mentioned in a comment"
                    description="Someone tags you in a comment"
                    value={preferences.events.mentions}
                    onChange={(v) => updateEvent('mentions', v)}
                    disabled={!isEnterprise}
                    disabledText="Enterprise"
                  />
                  <ToggleRow
                    icon={UserCheck}
                    label="A comment is assigned to me"
                    description="Action required on a comment"
                    value={preferences.events.assigned}
                    onChange={(v) => updateEvent('assigned', v)}
                  />
                  <ToggleRow
                    icon={MessageCircle}
                    label="Someone replies to my comment"
                    description="Activity on threads you're part of"
                    value={preferences.events.replies}
                    onChange={(v) => updateEvent('replies', v)}
                  />
                  <ToggleRow
                    icon={Pin}
                    label="A new comment is pinned"
                    description="Important comments pinned by PMO"
                    value={preferences.events.new_pins}
                    onChange={(v) => updateEvent('new_pins', v)}
                  />
                </div>
              </div>

              {/* Channel Preferences */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Notification Channels
                </h3>
                <div className="space-y-3">
                  <ToggleRow
                    icon={Bell}
                    label="In-app Inbox"
                    description="Always on - view in your inbox"
                    value={preferences.channels.in_app}
                    onChange={(v) => updateChannel('in_app', v)}
                    disabled={true}
                    alwaysOn={true}
                  />
                  <ToggleRow
                    icon={Mail}
                    label="Email notifications"
                    description="Receive email for important updates"
                    value={preferences.channels.email}
                    onChange={(v) => updateChannel('email', v)}
                    comingSoon={true}
                  />
                  <ToggleRow
                    icon={MessageSquare}
                    label="Microsoft Teams"
                    description="Get notified in Teams DM or channel"
                    value={preferences.channels.teams}
                    onChange={(v) => updateChannel('teams', v)}
                    disabled={!isEnterprise}
                    disabledText="Enterprise"
                  />
                  <ToggleRow
                    icon={Smartphone}
                    label="WhatsApp"
                    description="Receive WhatsApp notifications"
                    value={preferences.channels.whatsapp}
                    onChange={(v) => updateChannel('whatsapp', v)}
                    disabled={!isEnterprise}
                    disabledText="Enterprise"
                  />
                </div>
              </div>

              {/* Daily Signal */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  📊 Daily Signal™ Integration
                </h3>
                <ToggleRow
                  icon={Pin}
                  label="Include pinned comments"
                  description="Show recent pinned comments in Daily Signal"
                  value={preferences.daily_signal_comments}
                  onChange={(v) => setPreferences(prev => ({ ...prev, daily_signal_comments: v }))}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Preferences
          </button>
        </div>
      </div>
    </>
  );
};

// Toggle Row Component
const ToggleRow = ({ 
  icon: Icon, 
  label, 
  description, 
  value, 
  onChange, 
  disabled = false,
  disabledText = null,
  alwaysOn = false,
  comingSoon = false
}) => {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      disabled && !alwaysOn ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${disabled && !alwaysOn ? 'text-gray-300' : 'text-gray-500'}`} />
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${disabled && !alwaysOn ? 'text-gray-400' : 'text-gray-800'}`}>
              {label}
            </span>
            {disabledText && !alwaysOn && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                <Crown className="w-3 h-3" /> {disabledText}
              </span>
            )}
            {comingSoon && (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                Coming Soon
              </span>
            )}
            {alwaysOn && (
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                Always On
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
      <button
        onClick={() => !disabled && !comingSoon && onChange(!value)}
        disabled={disabled || comingSoon}
        className={`transition-colors ${disabled || comingSoon ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {value ? (
          <ToggleRight className={`w-8 h-8 ${disabled || comingSoon ? 'text-gray-300' : 'text-[var(--clr-brand-primary)]'}`} />
        ) : (
          <ToggleLeft className="w-8 h-8 text-gray-300" />
        )}
      </button>
    </div>
  );
};

export default NotificationPreferences;
